// src/views/condominios/CondominioModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { estadosBR, tiposCondominio } from "../../data/condominiosData";
import { integracoesService } from "../../services/condominiosService";
import condominiosService from "../../services/condominiosService";
import ConfirmModal from "../../components/ui/ConfirmModal";
import { useAuth } from "../../contexts/AuthContext";

const diasVenc = Array.from({ length: 28 }, (_, i) => String(i + 1));

function formatCNPJ(v) {
  const d = v.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}
function formatCPF(v) {
  const d=v.replace(/\D/g,"").slice(0,11);
  return d.replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d{1,2})$/,"$1-$2");
}

function formatPhone(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10)
    return d.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  return d.replace(/^(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
}

function formatCEP(v) {
  const d = v.replace(/\D/g, "").slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, "$1-$2");
}

function validarCNPJ(v) {
  const d = (v||"").replace(/\D/g,"");
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
  const calc = (n) => {
    let s = 0, p = n - 7;
    for (let i = 0; i < n; i++) { s += parseInt(d[i],10) * p; p = p === 2 ? 9 : p - 1; }
    const r = s % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === parseInt(d[12],10) && calc(13) === parseInt(d[13],10);
}
const emailValido = v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const EMPTY_CONFIG_APROV = { usaAprovacao: false, vlrLimiteAprov: "", observacao: "" };

export default function CondominioModal({ show, onClose, onSave, editData, saving = false, serverError=null }) {
  const isEdit = !!editData?.id;
  const fileInputRef = useRef(null);
  const { isAdmin, isSindico } = useAuth();
  const canConfigAprovacao = isAdmin || isSindico;

  const emptyForm = {
    nmCondominio: "", nmFantasia: "", cnpj: "", email: "", telefone: "",
    celular: "", dtCadastro: new Date().toISOString().split("T")[0],
    inativo: false, inscEstadual: "", inscMunicipal: "", contato: "", site: "",
    tpCondominio: "Residencial", tpJuro: "Mensal", juroDia: "1.00",
    tpMulta: "percentual", multaPerc: "2.00", diaVencimento: "10",
    tpFundoReserva: "Percentual", vlrFundoReserva: "5.00",
    cobCondominio: "rateioContas", vlrCondominio: "0.00",
    cep: "", cidade: "", uf: "SP", bairro: "", logradouro: "", numero: "", complemento: "",
    politicaFechamento: "Informativo", rateioBaseMes: "Vigente",
    // Síndico / branding / legal
    nmSindico: "", cpfSindico: "", telSindico: "", emailSindico: "",
    dtMandatoInicio: "", dtMandatoFim: "",
    logo: "", instrucoesBoleto: "",
    // Apólice (13)
    seguradoraNome: "", seguroApoliceNumero: "", seguroVigenciaIni: "", seguroVigenciaFim: "",
    seguroValorCobertura: "", seguroContato: "", seguroObservacao: "",
    // CNAE (14)
    cnaePrincipal: "", atividadeDesc: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [configAprov, setConfigAprov] = useState(EMPTY_CONFIG_APROV);
  const [configAprovLoading, setConfigAprovLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [cepLoading, setCepLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dados");

  function sanitizarData(dt) {
    if (!dt) return "";
    if (typeof dt === "string" && dt.includes("T")) return dt.split("T")[0];
    return dt;
  }

  useEffect(() => {
    if (show) {
      const base = editData ? { ...emptyForm, ...editData } : emptyForm;
      base.dtCadastro       = sanitizarData(base.dtCadastro) || new Date().toISOString().split("T")[0];
      base.dtMandatoInicio  = sanitizarData(base.dtMandatoInicio);
      base.dtMandatoFim     = sanitizarData(base.dtMandatoFim);
      base.seguroVigenciaIni = sanitizarData(base.seguroVigenciaIni);
      base.seguroVigenciaFim = sanitizarData(base.seguroVigenciaFim);
      setForm(base);
      setConfigAprov(EMPTY_CONFIG_APROV);
      setErrors({});
      setActiveTab("dados");
    }
    // eslint-disable-next-line
  }, [show, editData]);

  useEffect(() => {
    if (!show || !editData?.id) return;
    setConfigAprovLoading(true);
    condominiosService.buscarConfigAprovacao(editData.id)
      .then((r) => {
        const c = r.data || {};
        setConfigAprov({
          usaAprovacao: !!(c.usaAprovacao === true || c.usaAprovacao === 1),
          vlrLimiteAprov: c.vlrLimiteAprov != null ? String(c.vlrLimiteAprov) : "",
          observacao: c.observacao || "",
        });
      })
      .catch(() => setConfigAprov(EMPTY_CONFIG_APROV))
      .finally(() => setConfigAprovLoading(false));
  }, [show, editData?.id]);

  const [cnpjLoading, setCnpjLoading] = useState(false);
  const consultarCNPJ = async () => {
    const raw = (form.cnpj||"").replace(/\D/g,"");
    if (raw.length !== 14) {
      setErrors(p => ({ ...p, cnpj: "Informe um CNPJ válido (14 dígitos)." }));
      return;
    }
    setCnpjLoading(true);
    try {
      const r = await integracoesService.consultarCnpj(raw);
      const d = r.data;
      setForm(p => ({
        ...p,
        nmCondominio:  p.nmCondominio || d.razao_social || "",
        nmFantasia:    p.nmFantasia   || d.nome_fantasia || d.razao_social || "",
        email:         p.email        || (d.email||"").toLowerCase(),
        telefone:      p.telefone     || (d.ddd_telefone_1?formatPhone(d.ddd_telefone_1):""),
        cep:           formatCEP(d.cep||""),
        logradouro:    d.logradouro || p.logradouro,
        numero:        d.numero || p.numero,
        complemento:   d.complemento || p.complemento,
        bairro:        d.bairro || p.bairro,
        cidade:        d.municipio || p.cidade,
        uf:            d.uf || p.uf,
        cnaePrincipal: d.cnae_fiscal ? String(d.cnae_fiscal) : p.cnaePrincipal,
        atividadeDesc: d.cnae_fiscal_descricao || p.atividadeDesc,
      }));
      setErrors(p => ({ ...p, cnpj: undefined }));
    } catch(e) {
      setErrors(p => ({ ...p, cnpj: "Falha ao consultar CNPJ: " + (e?.message || "erro desconhecido") }));
    }
    setCnpjLoading(false);
  };

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const buscarCEP = async (cepRaw) => {
    const cep = cepRaw.replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((p) => ({
          ...p,
          logradouro: data.logradouro || p.logradouro,
          bairro: data.bairro || p.bairro,
          cidade: data.localidade || p.cidade,
          uf: data.uf || p.uf,
        }));
      }
    } catch (_) {}
    setCepLoading(false);
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setErrors(p => ({ ...p, logo: "Arquivo inválido — envie uma imagem (PNG/JPG)." }));
      return;
    }
    if (file.size > 500 * 1024) {
      setErrors(p => ({ ...p, logo: "Imagem muito grande — máx. 500KB." }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => set("logo", ev.target.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (!form.nmCondominio.trim()) e.nmCondominio = "Razão social é obrigatória";
    if (!form.cnpj.trim()) {
      e.cnpj = "CNPJ é obrigatório";
    } else if (!validarCNPJ(form.cnpj)) {
      e.cnpj = "CNPJ inválido — verifique os dígitos";
    }
    if (form.email && !emailValido(form.email)) e.email = "E-mail inválido";
    if (form.emailSindico && !emailValido(form.emailSindico)) e.emailSindico = "E-mail inválido";
    if (form.dtMandatoInicio && form.dtMandatoFim && form.dtMandatoFim < form.dtMandatoInicio) {
      e.dtMandatoFim = "Data fim do mandato anterior à data início";
    }
    setErrors(e);
    if (e.nmCondominio || e.cnpj || e.email) { setActiveTab("dados"); return false; }
    if (e.emailSindico || e.dtMandatoFim) { setActiveTab("sindico"); return false; }
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (configAprov.usaAprovacao && configAprov.vlrLimiteAprov !== "" && parseFloat(configAprov.vlrLimiteAprov) < 0) {
      setErrors(e => ({ ...e, vlrLimiteAprov: "Informe um valor zero ou positivo." }));
      setActiveTab("financeiro");
      return;
    }
    onSave({
      ...form,
      id: editData?.id,
      configAprovacao: canConfigAprovacao ? {
        usaAprovacao: !!configAprov.usaAprovacao,
        vlrLimiteAprov: configAprov.usaAprovacao
          ? (configAprov.vlrLimiteAprov === "" ? 0 : parseFloat(configAprov.vlrLimiteAprov || 0))
          : null,
        observacao: configAprov.observacao?.trim() || null,
      } : undefined,
    });
  };

  const setConfigAprovField = (k, v) => setConfigAprov(prev => ({ ...prev, [k]: v }));

  if (!show) return null;

  const tabs = [
    { id: "dados",      label: "Dados Gerais",     icon: "bi-building" },
    { id: "financeiro", label: "Financeiro",       icon: "bi-cash-coin" },
    { id: "endereco",   label: "Endereço",         icon: "bi-geo-alt" },
    { id: "sindico",    label: "Síndico / Mandato", icon: "bi-person-badge" },
    { id: "boleto",     label: "Boleto / Marca",   icon: "bi-receipt" },
    { id: "seguro",     label: "Seguro / Apólice", icon: "bi-shield-shaded" },
    { id: "docs",       label: "Documentos",       icon: "bi-file-earmark-pdf", disabled: !isEdit },
  ];

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.55)" }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div style={{ width:38, height:38, background:"rgba(255,255,255,.15)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="bi bi-buildings" style={{ fontSize:18, color:"#fff" }} />
              </div>
              <div>
                <h5 className="modal-title mb-0">
                  {isEdit ? "Editar Condomínio" : "Novo Condomínio"}
                </h5>
                <small style={{ color:"rgba(255,255,255,.6)", fontSize:12 }}>
                  {isEdit ? `Editando: ${editData.nmFantasia || editData.nmCondominio}` : "Preencha os dados do condomínio"}
                </small>
              </div>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>

          {/* Tabs */}
          <div style={{ background:"var(--primary)", padding:"0 24px" }}>
            <ul className="nav nav-tabs border-0" style={{ gap:4, flexWrap:"wrap" }}>
              {tabs.map(t => (
                <li className="nav-item" key={t.id}>
                  <button
                    className={`nav-link border-0 d-flex align-items-center gap-2 ${activeTab === t.id ? "active" : ""}`}
                    style={{
                      background: activeTab === t.id ? "#fff" : "transparent",
                      color: activeTab === t.id ? "var(--primary)" : "rgba(255,255,255,.7)",
                      borderRadius: "6px 6px 0 0",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      padding: "10px 18px",
                    }}
                    onClick={() => setActiveTab(t.id)}
                  >
                    <i className={`bi ${t.icon}`} />
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Body */}
          <div className="modal-body">
            {serverError && (
              <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize:13 }}>
                <i className="bi bi-exclamation-triangle-fill"/>
                <div>{serverError}</div>
              </div>
            )}

            {/* ───── TAB: DADOS GERAIS ───── */}
            {activeTab === "dados" && (
              <>
                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-building" /> Identificação
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Razão Social <span className="required-mark">*</span></label>
                      <input
                        className={`form-control ${errors.nmCondominio ? "is-invalid" : ""}`}
                        value={form.nmCondominio}
                        onChange={e => set("nmCondominio", e.target.value)}
                        placeholder="Nome completo do condomínio"
                      />
                      {errors.nmCondominio && <div className="invalid-feedback">{errors.nmCondominio}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Nome Fantasia</label>
                      <input className="form-control" value={form.nmFantasia} onChange={e => set("nmFantasia", e.target.value)} placeholder="Nome fantasia" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">CNPJ <span className="required-mark">*</span></label>
                      <div className="input-group">
                        <input
                          className={`form-control ${errors.cnpj ? "is-invalid" : (form.cnpj && validarCNPJ(form.cnpj)) ? "is-valid" : ""}`}
                          value={form.cnpj}
                          onChange={e => set("cnpj", formatCNPJ(e.target.value))}
                          placeholder="00.000.000/0001-00"
                        />
                        <button type="button" className="btn btn-outline-secondary" onClick={consultarCNPJ}
                          disabled={cnpjLoading || !validarCNPJ(form.cnpj)} title="Buscar dados na Receita (BrasilAPI)">
                          {cnpjLoading ? <span className="spinner-border spinner-border-sm"/> : <i className="bi bi-search"/>}
                        </button>
                      </div>
                      {errors.cnpj && <div className="invalid-feedback d-block">{errors.cnpj}</div>}
                      {!errors.cnpj && form.cnpj && validarCNPJ(form.cnpj) && <div className="text-success mt-1" style={{fontSize:12}}>CNPJ válido — clique na lupa para autopreencher.</div>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Inscrição Estadual</label>
                      <input className="form-control" value={form.inscEstadual||""} onChange={e => set("inscEstadual", e.target.value)} placeholder="Inscrição estadual" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Inscrição Municipal</label>
                      <input className="form-control" value={form.inscMunicipal||""} onChange={e => set("inscMunicipal", e.target.value)} placeholder="Inscrição municipal" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Tipo de Condomínio</label>
                      <select className="form-select" value={form.tpCondominio} onChange={e => set("tpCondominio", e.target.value)}>
                        {tiposCondominio.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>

                    {/* (14) CNAE / Atividade quando Comercial / Misto */}
                    {(form.tpCondominio === "Comercial" || form.tpCondominio === "Misto" || form.tpCondominio === "Lojas") && (
                      <>
                        <div className="col-md-3">
                          <label className="form-label">CNAE Principal</label>
                          <input className="form-control" value={form.cnaePrincipal||""} onChange={e=>set("cnaePrincipal", e.target.value.replace(/\D/g,"").slice(0,7))} placeholder="0000000"/>
                          <small className="text-muted" style={{fontSize:11}}>Apenas dígitos. Auto-preenche pelo CNPJ.</small>
                        </div>
                        <div className="col-md-9">
                          <label className="form-label">Descrição da atividade</label>
                          <input className="form-control" value={form.atividadeDesc||""} onChange={e=>set("atividadeDesc", e.target.value)} placeholder="Ex: Aluguel de imóveis próprios"/>
                        </div>
                      </>
                    )}
                    <div className="col-md-3">
                      <label className="form-label">Data de Cadastro</label>
                      <input type="date" className="form-control" value={form.dtCadastro} onChange={e => set("dtCadastro", e.target.value)} />
                    </div>
                    <div className="col-md-3 d-flex align-items-end pb-1">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="inativo"
                          checked={form.inativo}
                          onChange={e => set("inativo", e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="inativo" style={{ fontSize:13, fontWeight:600, color:"var(--gray-600)" }}>
                          Inativo
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-telephone" /> Contato
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">E-mail</label>
                      <input
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        type="email"
                        value={form.email||""}
                        onChange={e => set("email", e.target.value.toLowerCase().trim())}
                        placeholder="contato@condominio.com.br"
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Telefone</label>
                      <input className="form-control" value={form.telefone||""} onChange={e => set("telefone", formatPhone(e.target.value))} placeholder="(00) 0000-0000" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Celular</label>
                      <input className="form-control" value={form.celular||""} onChange={e => set("celular", formatPhone(e.target.value))} placeholder="(00) 00000-0000" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Pessoa de Contato</label>
                      <input className="form-control" value={form.contato||""} onChange={e => set("contato", e.target.value)} placeholder="Nome do responsável" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Site</label>
                      <input className="form-control" value={form.site||""} onChange={e => set("site", e.target.value)} placeholder="www.condominio.com.br" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ───── TAB: FINANCEIRO ───── */}
            {activeTab === "financeiro" && (
              <>
                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-percent" /> Juros e Multas
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Tipo de Juro</label>
                      <select className="form-select" value={form.tpJuro} onChange={e => set("tpJuro", e.target.value)}>
                        <option value="Mensal">Mensal</option>
                        <option value="Diario">Diário</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        {form.tpJuro === "Mensal" ? "Juro Mensal (%)" : "Juro Diário (%)"}
                      </label>
                      <div className="input-group">
                        <input
                          className="form-control"
                          type="number"
                          step="0.001"
                          min="0"
                          value={form.juroDia}
                          onChange={e => set("juroDia", e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Política de Fechamento</label>
                      <select className="form-select" value={form.politicaFechamento||"Informativo"}
                        onChange={e=>set("politicaFechamento",e.target.value)}>
                        <option value="Informativo">Informativo (permite fechar com pendências)</option>
                        <option value="Bloqueante">Bloqueante (exige quitar pendências)</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Rateio por Contas — Base</label>
                      <select className="form-select" value={form.rateioBaseMes||"Vigente"}
                        onChange={e=>set("rateioBaseMes",e.target.value)}>
                        <option value="Vigente">Mês Vigente (despesas do mês atual)</option>
                        <option value="Anterior">Mês Anterior (despesas do mês fechado)</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Dia de Vencimento</label>
                      <select className="form-select" value={form.diaVencimento} onChange={e => set("diaVencimento", e.target.value)}>
                        {diasVenc.map(d => <option key={d} value={d}>Dia {d.padStart(2,"0")}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tipo de Multa</label>
                      <select className="form-select" value={form.tpMulta} onChange={e => set("tpMulta", e.target.value)}>
                        <option value="percentual">Percentual (%)</option>
                        <option value="vlrFixo">Valor Fixo (R$)</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        {form.tpMulta === "percentual" ? "Multa (%)" : "Multa (R$)"}
                      </label>
                      <div className="input-group">
                        {form.tpMulta === "percentual"
                          ? <><input className="form-control" type="number" step="0.01" min="0" value={form.multaPerc} onChange={e => set("multaPerc", e.target.value)} /><span className="input-group-text">%</span></>
                          : <><span className="input-group-text">R$</span><input className="form-control" type="number" step="0.01" min="0" value={form.multaPerc} onChange={e => set("multaPerc", e.target.value)} /></>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-check2-square" /> Aprovação de Contas a Pagar
                  </div>
                  {configAprovLoading ? (
                    <div className="text-muted small py-2">
                      <span className="spinner-border spinner-border-sm me-2"/>Carregando configuração…
                    </div>
                  ) : (
                    <>
                      <p className="text-muted small mb-3">
                        Quando ativo, lançamentos acima do valor mínimo ficam <strong>Pendentes</strong> até o síndico ou administrador aprovar em Contas a Pagar.
                        Use <strong>R$ 0,00</strong> no limite para exigir aprovação em todas as despesas.
                      </p>
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="usaAprovacaoCp"
                              checked={!!configAprov.usaAprovacao}
                              disabled={!canConfigAprovacao}
                              onChange={e => setConfigAprovField("usaAprovacao", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="usaAprovacaoCp">
                              Exigir aprovação antes do pagamento
                            </label>
                          </div>
                          {!canConfigAprovacao && (
                            <div className="form-text">Somente Admin ou Síndico podem alterar esta configuração.</div>
                          )}
                        </div>
                        {configAprov.usaAprovacao && (
                          <>
                            <div className="col-md-4">
                              <label className="form-label">Valor mínimo para exigir aprovação (R$)</label>
                              <div className="input-group">
                                <span className="input-group-text">R$</span>
                                <input
                                  className={`form-control${errors.vlrLimiteAprov ? " is-invalid" : ""}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0 = todas as despesas"
                                  value={configAprov.vlrLimiteAprov}
                                  disabled={!canConfigAprovacao}
                                  onChange={e => setConfigAprovField("vlrLimiteAprov", e.target.value)}
                                />
                              </div>
                              {errors.vlrLimiteAprov && <div className="invalid-feedback d-block">{errors.vlrLimiteAprov}</div>}
                              <div className="form-text">Abaixo deste valor o lançamento segue sem aprovação (Dispensado).</div>
                            </div>
                            <div className="col-md-8">
                              <label className="form-label">Observação interna (opcional)</label>
                              <input
                                className="form-control"
                                maxLength={300}
                                placeholder="Ex.: Aprovação obrigatória acima de R$ 500"
                                value={configAprov.observacao}
                                disabled={!canConfigAprovacao}
                                onChange={e => setConfigAprovField("observacao", e.target.value)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-safe" /> Fundo de Reserva
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Tipo do Fundo de Reserva</label>
                      <select className="form-select" value={form.tpFundoReserva} onChange={e => set("tpFundoReserva", e.target.value)}>
                        <option value="Percentual">Percentual (%)</option>
                        <option value="valorFixo">Valor Fixo (R$)</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        {form.tpFundoReserva === "Percentual" ? "Percentual do Fundo (%)" : "Valor do Fundo (R$)"}
                      </label>
                      <div className="input-group">
                        {form.tpFundoReserva === "Percentual"
                          ? <><input className="form-control" type="number" step="0.01" min="0" value={form.vlrFundoReserva} onChange={e => set("vlrFundoReserva", e.target.value)} /><span className="input-group-text">%</span></>
                          : <><span className="input-group-text">R$</span><input className="form-control" type="number" step="0.01" min="0" value={form.vlrFundoReserva} onChange={e => set("vlrFundoReserva", e.target.value)} /></>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-receipt" /> Cobrança do Condomínio
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Tipo de Cobrança</label>
                      <select className="form-select" value={form.cobCondominio} onChange={e => set("cobCondominio", e.target.value)}>
                        <option value="rateioContas">Rateio de Contas</option>
                        <option value="vlrFixo">Valor Fixo</option>
                      </select>
                    </div>
                    {form.cobCondominio === "vlrFixo" && (
                      <div className="col-md-4">
                        <label className="form-label">Valor do Condomínio (R$)</label>
                        <div className="input-group">
                          <span className="input-group-text">R$</span>
                          <input
                            className="form-control"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.vlrCondominio}
                            onChange={e => set("vlrCondominio", e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                    {form.cobCondominio === "rateioContas" && (
                      <div className="col-md-8">
                        <div style={{ background:"var(--gray-100)", borderRadius:8, padding:"12px 16px", marginTop:4 }}>
                          <i className="bi bi-info-circle text-primary me-2" />
                          <span style={{ fontSize:13, color:"var(--gray-600)" }}>
                            No modo <strong>Rateio de Contas</strong>, o valor do condomínio é calculado automaticamente com base nas despesas divididas pelas unidades.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ───── TAB: ENDEREÇO ───── */}
            {activeTab === "endereco" && (
              <div className="form-section">
                <div className="form-section-title">
                  <i className="bi bi-geo-alt" /> Endereço
                </div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">CEP</label>
                    <div className="input-group">
                      <input
                        className="form-control"
                        value={form.cep||""}
                        onChange={e => set("cep", formatCEP(e.target.value))}
                        onBlur={e => buscarCEP(e.target.value)}
                        placeholder="00000-000"
                        maxLength={9}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => buscarCEP(form.cep)}
                        disabled={cepLoading}
                        title="Buscar CEP"
                      >
                        {cepLoading
                          ? <span className="spinner-border spinner-border-sm" />
                          : <i className="bi bi-search" />
                        }
                      </button>
                    </div>
                    <small style={{ color:"var(--gray-400)", fontSize:11 }}>Preencha para buscar o endereço automaticamente</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Logradouro</label>
                    <input className="form-control" value={form.logradouro||""} onChange={e => set("logradouro", e.target.value)} placeholder="Rua, Avenida, etc." />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Número</label>
                    <input className="form-control" value={form.numero||""} onChange={e => set("numero", e.target.value)} placeholder="Nº" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Complemento</label>
                    <input className="form-control" value={form.complemento||""} onChange={e => set("complemento", e.target.value)} placeholder="Apto, Bloco, etc." />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Bairro</label>
                    <input className="form-control" value={form.bairro||""} onChange={e => set("bairro", e.target.value)} placeholder="Bairro" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Cidade</label>
                    <input className="form-control" value={form.cidade||""} onChange={e => set("cidade", e.target.value)} placeholder="Cidade" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">UF</label>
                    <select className="form-select" value={form.uf||"SP"} onChange={e => set("uf", e.target.value)}>
                      {estadosBR.map(uf => <option key={uf}>{uf}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ───── TAB: SÍNDICO / MANDATO ───── */}
            {activeTab === "sindico" && (
              <div className="form-section">
                <div className="form-section-title">
                  <i className="bi bi-person-badge" /> Dados do Síndico
                </div>
                <div className="alert alert-light border" style={{fontSize:12}}>
                  <i className="bi bi-info-circle me-2 text-primary"/>
                  Aparecem em atas de assembleia, comunicados oficiais e relatórios financeiros.
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Nome do Síndico</label>
                    <input className="form-control" value={form.nmSindico||""} onChange={e=>set("nmSindico", e.target.value)} placeholder="Nome completo do síndico"/>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">CPF do Síndico</label>
                    <input className="form-control" value={form.cpfSindico||""} onChange={e=>set("cpfSindico", formatCPF(e.target.value))} placeholder="000.000.000-00"/>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Telefone</label>
                    <input className="form-control" value={form.telSindico||""} onChange={e=>set("telSindico", formatPhone(e.target.value))} placeholder="(00) 00000-0000"/>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">E-mail do Síndico</label>
                    <input className={`form-control ${errors.emailSindico?"is-invalid":""}`} type="email" value={form.emailSindico||""} onChange={e=>set("emailSindico", e.target.value.toLowerCase().trim())} placeholder="sindico@condominio.com.br"/>
                    {errors.emailSindico && <div className="invalid-feedback">{errors.emailSindico}</div>}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Início do Mandato</label>
                    <input type="date" className="form-control" value={form.dtMandatoInicio||""} onChange={e=>set("dtMandatoInicio", e.target.value)}/>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Fim do Mandato</label>
                    <input type="date" className={`form-control ${errors.dtMandatoFim?"is-invalid":""}`} value={form.dtMandatoFim||""} onChange={e=>set("dtMandatoFim", e.target.value)}/>
                    {errors.dtMandatoFim && <div className="invalid-feedback">{errors.dtMandatoFim}</div>}
                  </div>
                </div>
              </div>
            )}

            {/* ───── TAB: BOLETO / MARCA ───── */}
            {activeTab === "boleto" && (
              <>
                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-image" /> Logotipo
                  </div>
                  <div className="row g-3 align-items-center">
                    <div className="col-md-3 text-center">
                      <div style={{ width:140, height:140, borderRadius:8, background:"var(--gray-100)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", border:"1px dashed var(--gray-300)" }}>
                        {form.logo
                          ? <img src={form.logo} alt="Logo" style={{ maxWidth:"100%", maxHeight:"100%" }} />
                          : <i className="bi bi-image" style={{ fontSize:48, color:"var(--gray-400)" }} />}
                      </div>
                    </div>
                    <div className="col-md-9">
                      <label className="form-label">Logo do Condomínio</label>
                      <input ref={fileInputRef} type="file" accept="image/*" className="form-control" onChange={onLogoUpload}/>
                      <small style={{ color:"var(--gray-400)", fontSize:11 }}>Imagem PNG/JPG, máx. 500KB. Aparece em boletos, atas e comunicados.</small>
                      {errors.logo && <div className="text-danger mt-1" style={{fontSize:12}}>{errors.logo}</div>}
                      {form.logo && (
                        <button type="button" className="btn btn-outline-danger btn-sm mt-2" onClick={()=>{ set("logo",""); if(fileInputRef.current) fileInputRef.current.value=""; }}>
                          <i className="bi bi-trash3"/> Remover logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title">
                    <i className="bi bi-file-earmark-text" /> Instruções do Boleto
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Texto exibido nos boletos
                        <small style={{color:"var(--gray-400)",marginLeft:6,fontSize:11,fontWeight:400}}>(impresso na parte de instruções)</small>
                      </label>
                      <textarea className="form-control" rows={5}
                        value={form.instrucoesBoleto||""}
                        onChange={e=>set("instrucoesBoleto", e.target.value.slice(0, 1000))}
                        placeholder="Ex: Após o vencimento, cobrar multa de 2% e juros de 1% ao mês. Não receber após 60 dias do vencimento."/>
                      <small className="text-muted">{(form.instrucoesBoleto||"").length}/1000 caracteres</small>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ───── TAB: SEGURO / APÓLICE ───── */}
            {activeTab === "seguro" && (
              <>
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-shield-shaded"/> Apólice de Seguro</div>
                  <div className="alert alert-light border" style={{fontSize:12}}>
                    <i className="bi bi-bell text-warning me-2"/>
                    Aviso automático por e-mail ao síndico quando a vigência estiver a 30 dias do vencimento (toda segunda-feira às 08:00).
                  </div>
                  <div className="row g-3">
                    <div className="col-md-5">
                      <label className="form-label">Seguradora</label>
                      <input className="form-control" value={form.seguradoraNome||""} onChange={e=>set("seguradoraNome",e.target.value)} placeholder="Ex: Porto Seguro"/>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Nº da Apólice</label>
                      <input className="form-control" value={form.seguroApoliceNumero||""} onChange={e=>set("seguroApoliceNumero",e.target.value)} placeholder="000000000"/>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Valor da Cobertura (R$)</label>
                      <input className="form-control" type="number" step="0.01" value={form.seguroValorCobertura||""} onChange={e=>set("seguroValorCobertura",e.target.value)} placeholder="0.00"/>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Vigência início</label>
                      <input type="date" className="form-control" value={form.seguroVigenciaIni||""} onChange={e=>set("seguroVigenciaIni",e.target.value)}/>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Vigência fim</label>
                      <input type="date" className={`form-control ${form.seguroVigenciaFim && form.seguroVigenciaIni && form.seguroVigenciaFim<=form.seguroVigenciaIni?"is-invalid":""}`} value={form.seguroVigenciaFim||""} onChange={e=>set("seguroVigenciaFim",e.target.value)}/>
                      {form.seguroVigenciaFim && form.seguroVigenciaIni && form.seguroVigenciaFim<=form.seguroVigenciaIni && (
                        <div className="invalid-feedback d-block">A vigência fim deve ser posterior à de início.</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Contato (telefone/email do corretor)</label>
                      <input className="form-control" value={form.seguroContato||""} onChange={e=>set("seguroContato",e.target.value)} placeholder="Ex: (11) 4000-0000 — corretor@x.com"/>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Observações</label>
                      <textarea className="form-control" rows={3} value={form.seguroObservacao||""} onChange={e=>set("seguroObservacao",e.target.value)} placeholder="Cláusulas, franquia, coberturas adicionais..."/>
                    </div>
                  </div>
                  {form.seguroVigenciaFim && (
                    (()=>{
                      const hoje = new Date(); hoje.setHours(0,0,0,0);
                      const fim = new Date(form.seguroVigenciaFim+"T12:00:00");
                      const dias = Math.ceil((fim-hoje)/86400000);
                      if (dias < 0)  return <div className="alert alert-danger mt-3 py-2" style={{fontSize:13}}><i className="bi bi-x-circle-fill me-2"/>Apólice <strong>vencida há {Math.abs(dias)} dia(s)</strong>.</div>;
                      if (dias <= 30) return <div className="alert alert-warning mt-3 py-2" style={{fontSize:13}}><i className="bi bi-exclamation-triangle-fill me-2"/>Apólice vence em <strong>{dias} dia(s)</strong>.</div>;
                      return <div className="alert alert-success mt-3 py-2" style={{fontSize:13}}><i className="bi bi-check-circle-fill me-2"/>Apólice vigente — vence em {dias} dia(s).</div>;
                    })()
                  )}
                </div>
              </>
            )}

            {/* ───── TAB: DOCUMENTOS ───── */}
            {activeTab === "docs" && (
              <DocumentosCondominio idCondominio={editData?.id}/>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer d-flex justify-content-between">
            <div style={{ fontSize:12, color:"var(--gray-400)" }}>
              <i className="bi bi-asterisk me-1" style={{ color:"#ef4444", fontSize:9 }} />
              Campos obrigatórios
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                <i className="bi bi-x-circle" /> Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                  : <><i className="bi bi-check-circle" /> {isEdit ? "Salvar Alterações" : "Cadastrar"}</>
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Subcomponente: Documentos do Condomínio (Convenção, Regimento, Apólice, etc.)
// ──────────────────────────────────────────────────────────────────────────
const TIPOS_DOC_CONDO = ["Convencao","Regimento","ApoliceSeguro","AtaAssembleia","HabiteSe","CertidaoNegativa","Outros"];

function DocumentosCondominio({ idCondominio }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [tipo, setTipo] = useState("Convencao");
  const [titulo, setTitulo] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [visivelPortal, setVisivelPortal] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [confirmacao, setConfirmacao] = useState(null);

  const carregar = async () => {
    if (!idCondominio) return;
    setLoading(true); setErro(null);
    try { const r = await condominiosService.listarDocumentos(idCondominio); setDocs(r.data||[]); }
    catch(e){ setErro(e.message); }
    setLoading(false);
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [idCondominio]);

  const enviar = async () => {
    if (!arquivo) return setErro("Selecione um arquivo.");
    if (!titulo.trim()) return setErro("Informe o título do documento.");
    if (arquivo.size > 10*1024*1024) return setErro("Arquivo excede 10MB.");
    setEnviando(true); setErro(null);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          await condominiosService.uploadDocumento(idCondominio, {
            tipo, titulo: titulo.trim(),
            nomeArquivo: arquivo.name, mimeType: arquivo.type, tamanho: arquivo.size,
            visivelPortal: visivelPortal ? 1 : 0,
            arquivo: ev.target.result,
          });
          setTitulo(""); setArquivo(null);
          await carregar();
        } catch(e) { setErro(e.message); }
        setEnviando(false);
      };
      reader.readAsDataURL(arquivo);
    } catch(e) { setErro(e.message); setEnviando(false); }
  };

  const baixar = async (d) => {
    try {
      const r = await condominiosService.baixarDocumento(idCondominio, d.id);
      const a = document.createElement("a");
      a.href = r.data.arquivo;
      a.download = r.data.nomeArquivo || "documento";
      document.body.appendChild(a); a.click(); a.remove();
    } catch(e) { setErro(e.message); }
  };

  const excluir = (d) => {
    setConfirmacao({
      titulo: "Excluir documento?",
      mensagem: `Deseja realmente excluir o documento "${d.titulo}"?`,
      textoOk: "Excluir",
      perigo: true,
      onConfirm: async () => {
        setConfirmacao(null);
        try { await condominiosService.excluirDocumento(idCondominio, d.id); await carregar(); }
        catch(e) { setErro(e.message); }
      },
    });
  };

  if (!idCondominio) {
    return <div className="alert alert-info py-2" style={{fontSize:13}}>
      <i className="bi bi-info-circle me-2"/>Salve o condomínio antes de anexar documentos.
    </div>;
  }

  return (
    <div className="form-section">
      <div className="form-section-title"><i className="bi bi-file-earmark-pdf"/> Documentos do Condomínio</div>
      <div className="alert alert-light border" style={{fontSize:12}}>
        <i className="bi bi-info-circle me-2 text-primary"/>
        Convenção e Regimento marcados como "visíveis no portal" ficam disponíveis para download pelos condôminos.
      </div>
      {erro && <div className="alert alert-danger py-2" style={{fontSize:13}}><i className="bi bi-exclamation-triangle-fill me-2"/>{erro}</div>}

      <div style={{background:"var(--bg-surface-alt)",border:"1px solid var(--border-color)",borderRadius:8,padding:12,marginBottom:14}}>
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label" style={{fontSize:12}}>Tipo</label>
            <select className="form-select form-select-sm" value={tipo} onChange={e=>setTipo(e.target.value)}>
              {TIPOS_DOC_CONDO.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label" style={{fontSize:12}}>Título *</label>
            <input className="form-control form-control-sm" value={titulo} onChange={e=>setTitulo(e.target.value)} placeholder="Ex: Convenção registrada em 2022"/>
          </div>
          <div className="col-md-3">
            <label className="form-label" style={{fontSize:12}}>Arquivo (até 10MB)</label>
            <input type="file" className="form-control form-control-sm" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e=>setArquivo(e.target.files?.[0]||null)}/>
          </div>
          <div className="col-md-2">
            <div className="form-check mb-1">
              <input className="form-check-input" type="checkbox" id="visPortal" checked={visivelPortal} onChange={e=>setVisivelPortal(e.target.checked)}/>
              <label className="form-check-label" htmlFor="visPortal" style={{fontSize:11}}>Visível no portal</label>
            </div>
            <button className="btn btn-success btn-sm w-100" onClick={enviar} disabled={enviando||!arquivo||!titulo.trim()}>
              {enviando ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-upload"/> Enviar</>}
            </button>
          </div>
        </div>
      </div>

      {loading ? <div className="text-center py-3"><span className="spinner-border spinner-border-sm me-2"/>Carregando...</div>
        : docs.length === 0 ? <div className="text-center py-3 text-muted" style={{fontSize:13}}>Nenhum documento.</div>
        : <table className="table table-custom table-sm">
            <thead><tr><th>Tipo</th><th>Título</th><th>Arquivo</th><th>Tamanho</th><th>Portal</th><th>Enviado em</th><th style={{width:80}}>Ações</th></tr></thead>
            <tbody>
              {docs.map(d=>(
                <tr key={d.id}>
                  <td><span className="badge text-bg-light border">{d.tipo}</span></td>
                  <td style={{fontSize:12.5,fontWeight:600}}>{d.titulo}</td>
                  <td style={{fontSize:12}}><i className="bi bi-file-earmark me-1 text-primary"/>{d.nomeArquivo}</td>
                  <td style={{fontSize:12}}>{d.tamanho ? `${(d.tamanho/1024).toFixed(1)} KB` : "—"}</td>
                  <td>{d.visivelPortal ? <i className="bi bi-eye text-success"/> : <i className="bi bi-eye-slash text-secondary"/>}</td>
                  <td style={{fontSize:12}}>{new Date(d.createdAt).toLocaleString("pt-BR")}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-outline-primary btn-sm btn-icon" title="Baixar" onClick={()=>baixar(d)}><i className="bi bi-download"/></button>
                      <button className="btn btn-outline-danger btn-sm btn-icon" title="Excluir" onClick={()=>excluir(d)}><i className="bi bi-trash3"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      }

      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoOk={confirmacao.textoOk}
          perigo={confirmacao.perigo}
          onConfirm={confirmacao.onConfirm}
          onCancel={() => setConfirmacao(null)}
        />
      )}
    </div>
  );
}
